import forge from 'node-forge';
import aesjs from 'aes-js';

// Port of midani-frontend's src/config/security.ts (kept framework-agnostic —
// pure crypto helpers only; the handshake orchestration lives in
// actions/handshake.js). The scheme:
//  - Requests: AES-256-CBC encrypt the JSON body with a random key/iv, then
//    RSA-OAEP (SHA-1) wrap the key/iv with the server's public key → { value, handler }.
//  - Responses: RSA-OAEP decrypt the handler to recover key/iv, then AES-decrypt value.

// SERVER DECRYPTS WITH SERVER PRIVATE KEY, WE ENCRYPT WITH SERVER PUBLIC KEY
function _arrayBufferToBase64(buffer) {
  var binary = '';
  var bytes = new Uint8Array(buffer);
  var len = bytes.byteLength;
  for (var i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

const rsaEncryption = (publicKeyPem, key_256, iv) => {
  const publicKey = forge.pki.publicKeyFromPem(publicKeyPem);

  // Prepare the data to encrypt (key and iv)
  const buffer = {
    key: _arrayBufferToBase64(key_256),
    iv: _arrayBufferToBase64(iv),
  };

  // Encrypt the data with the public key (RSA encryption)
  const encrypted = publicKey.encrypt(forge.util.encodeUtf8(JSON.stringify(buffer)), 'RSA-OAEP');

  return forge.util.encode64(encrypted);
};

const encryptApiData = async (publicKeyPem, data) => {
  const key_256 = new Uint8Array(32); // 256 bits
  const iv = new Uint8Array(16); // 128 bits

  window.crypto.getRandomValues(key_256); // Generates a random 256-bit key
  window.crypto.getRandomValues(iv);

  var textBytes = aesjs.utils.utf8.toBytes(JSON.stringify(data));
  var aesCbc = new aesjs.ModeOfOperation.cbc(key_256, iv);
  var encryptedBytes = aesCbc.encrypt(aesjs.padding.pkcs7.pad(textBytes));
  var encryptedBase64 = _arrayBufferToBase64(encryptedBytes);
  const sendData = {
    value: encryptedBase64,
    handler: rsaEncryption(publicKeyPem, key_256, iv),
  };
  return sendData;
};

// SERVER ENCRYPTS WITH OUR PUBLIC KEY, WE DECRYPT WITH OUR PRIVATE KEY
async function generateRSAKeys() {
  const keyPair = forge.pki.rsa.generateKeyPair(2048);
  const publicKey = forge.pki.publicKeyToPem(keyPair.publicKey);
  const privateKey = forge.pki.privateKeyToPem(keyPair.privateKey);
  return { publicKey, privateKey };
}

const base64ToUint8Array = (base64) => {
  const binaryString = atob(base64);
  const length = binaryString.length;
  const uint8Array = new Uint8Array(length);

  for (let i = 0; i < length; i++) {
    uint8Array[i] = binaryString.charCodeAt(i);
  }

  return uint8Array;
};

const decryptData = (data, rsaData) => {
  const keyBytes = base64ToUint8Array(rsaData.key);
  const ivBytes = base64ToUint8Array(rsaData.iv);
  const aesCbc = new aesjs.ModeOfOperation.cbc(keyBytes, ivBytes);
  const encryptedBytes = base64ToUint8Array(data);
  const decryptedBytes = aesjs.padding.pkcs7.strip(aesCbc.decrypt(encryptedBytes));
  const decryptedText = aesjs.utils.utf8.fromBytes(decryptedBytes);
  const parsedData = JSON.parse(decryptedText);
  return parsedData;
};

const decryptApiData = async (pkcs1Pem, data) => {
  let handler = data.handler;
  let handlerValue = data.value;
  const privateKey = forge.pki.privateKeyFromPem(pkcs1Pem);
  const pkcs8Pem = forge.pki.privateKeyToPem(privateKey);
  const privateKeyPem = forge.pki.privateKeyFromPem(pkcs8Pem);
  const decrypted = JSON.parse(privateKeyPem.decrypt(forge.util.decode64(handler), 'RSA-OAEP'));
  const apiData = await decryptData(handlerValue, decrypted);
  return apiData;
};

export { generateRSAKeys, encryptApiData, decryptApiData };
